// convex/lib/config/domains/catalog.ts

import { validatorProductService } from "../../../companies/products";
import { validatorCategory } from "../../../companies/configuration/categories";
import { validatorBrand } from "../../../companies/configuration/brands";
import { validatorSupplier } from "../../../companies/suppliers";
import { validatorService } from "../../../companies/services";
import { validatorUsers } from "../../../system/users";
import {
  productTypeRule,
  productCategoryRule,
  productBrandRule,
} from "../../../companies/products/filter_rules";
import { supplierStateRule } from "../../../companies/suppliers/filter_rules";
import { createTagFilterRule } from "../../../companies/tags/filter_rules";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// CATALOG DOMAIN - Status Values for i18n
// ============================================================================

export const PRODUCT_TYPES = ["product", "service"] as const;
export const PRODUCT_STATUSES = ["active", "inactive", "discontinued"] as const;
export const SERVICE_STATUSES = ["active", "inactive", "discontinued"] as const;
export const SUPPLIER_STATUSES = ["active", "inactive", "blocked"] as const;
export const CATEGORY_STATUSES = ["active", "inactive"] as const;
export const BRAND_STATUSES = ["active", "inactive"] as const;

export const catalogDomain: DomainGroup = {
  id: "catalog",
  name: "Catálogo",
  description:
    "Gestión de productos, servicios, categorías, marcas y proveedores",
  icon: "Package",
  order: 3,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "catalog",
    keys: {
      title: "catalog.title",
      description: "catalog.description",
    },
  },

  modules: [
    {
      id: "products",
      name: "Productos",
      description: "Gestión de productos y servicios del catálogo",
      icon: "Package",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/products"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 8,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "catalog.products",
        keys: {
          title: "catalog.products.title",
          description: "catalog.products.description",
          longDescription: "catalog.products.longDescription",
          searchPlaceholder: "catalog.products.searchPlaceholder",
          emptyState: "catalog.products.emptyState",
          actions: {
            create: "catalog.products.actions.create",
            edit: "catalog.products.actions.edit",
            delete: "catalog.products.actions.delete",
            view: "catalog.products.actions.view",
          },
        },
        translatableFields: ["name", "description"],
        translatableEnums: [
          { field: "type", values: PRODUCT_TYPES },
          { field: "status", values: PRODUCT_STATUSES },
        ],
      },

      tables: [
        {
          tableName: "products",
          schema: validatorProductService,
          scriptFieldAliases: {
            status: "internalControl.state",
            createdAt: "internalControl.createdAt",
            updatedAt: "internalControl.updatedAt",
          },
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "brand",
              schema: validatorBrand,
              tableName: "brands",
            },
            {
              key: "supplier",
              schema: validatorSupplier,
              tableName: "suppliers",
            },
            {
              key: "productCategories",
              sourceField: "categories",
              schema: validatorCategory,
              tableName: "categories",
              many: true,
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "code", "description", "barcode"],
          hasStatusIndex: true,
          customRules: [productTypeRule, productCategoryRule, productBrandRule, createTagFilterRule("products")],
          withTags: true,
        },
      ],
      readOnlyTables: [
        { tableName: "brands" },
        { tableName: "categories" },
        { tableName: "suppliers" },
      ],
    },
    {
      id: "services",
      name: "Servicios",
      description: "Gestión de servicios disponibles",
      icon: "Wrench",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/services"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 7,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "catalog.services",
        keys: {
          title: "catalog.services.title",
          description: "catalog.services.description",
          longDescription: "catalog.services.longDescription",
          searchPlaceholder: "catalog.services.searchPlaceholder",
          emptyState: "catalog.services.emptyState",
          actions: {
            create: "catalog.services.actions.create",
            edit: "catalog.services.actions.edit",
            delete: "catalog.services.actions.delete",
            view: "catalog.services.actions.view",
          },
        },
        translatableFields: ["name", "description"],
        translatableEnums: [{ field: "status", values: SERVICE_STATUSES }],
      },

      tables: [
        {
          tableName: "services",
          schema: validatorService,
          scriptActions: ["create", "update"],
          scriptFieldAliases: {
            status: "internalControl.state",
            createdAt: "internalControl.createdAt",
            updatedAt: "internalControl.updatedAt",
          },
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "allCategories",
              schema: validatorCategory,
              sourceField: "categories",
              tableName: "categories",
              many: true,
            },
            {
              key: "brand",
              schema: validatorBrand,
              tableName: "brands",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "code", "description"],
          hasStatusIndex: true,
          customRules: [createTagFilterRule("services")],
          withTags: true,
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "categories",
      name: "Categorías",
      description: "Gestión de categorías de productos",
      icon: "Grid3X3",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: [],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 10,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "catalog.categories",
        keys: {
          title: "catalog.categories.title",
          description: "catalog.categories.description",
          longDescription: "catalog.categories.longDescription",
          searchPlaceholder: "catalog.categories.searchPlaceholder",
          emptyState: "catalog.categories.emptyState",
          actions: {
            create: "catalog.categories.actions.create",
            edit: "catalog.categories.actions.edit",
            delete: "catalog.categories.actions.delete",
            view: "catalog.categories.actions.view",
          },
        },
        translatableFields: ["name", "description"],
        translatableEnums: [{ field: "status", values: CATEGORY_STATUSES }],
      },

      tables: [
        {
          tableName: "categories",
          schema: validatorCategory,
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "user",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "description"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "brands",
      name: "Marcas",
      description: "Gestión de marcas y fabricantes",
      icon: "Tag",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: [],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 11,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "catalog.brands",
        keys: {
          title: "catalog.brands.title",
          description: "catalog.brands.description",
          longDescription: "catalog.brands.longDescription",
          searchPlaceholder: "catalog.brands.searchPlaceholder",
          emptyState: "catalog.brands.emptyState",
          actions: {
            create: "catalog.brands.actions.create",
            edit: "catalog.brands.actions.edit",
            delete: "catalog.brands.actions.delete",
            view: "catalog.brands.actions.view",
          },
        },
        translatableFields: ["name"],
        translatableEnums: [{ field: "status", values: BRAND_STATUSES }],
      },

      tables: [
        {
          tableName: "brands",
          schema: validatorBrand,
          scope: "company" as TableScope,
          masterData: true,
          relations: [],
          searchFields: ["name"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "suppliers",
      name: "Proveedores",
      description: "Gestión de proveedores y abastecedores",
      icon: "Truck",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/suppliers"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 12,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "catalog.suppliers",
        keys: {
          title: "catalog.suppliers.title",
          description: "catalog.suppliers.description",
          longDescription: "catalog.suppliers.longDescription",
          searchPlaceholder: "catalog.suppliers.searchPlaceholder",
          emptyState: "catalog.suppliers.emptyState",
          actions: {
            create: "catalog.suppliers.actions.create",
            edit: "catalog.suppliers.actions.edit",
            delete: "catalog.suppliers.actions.delete",
            view: "catalog.suppliers.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [{ field: "status", values: SUPPLIER_STATUSES }],
      },

      tables: [
        {
          tableName: "suppliers",
          schema: validatorSupplier,
          scriptActions: ["create", "update"],
          scriptFieldAliases: {
            status: "internalControl.state",
            createdAt: "internalControl.createdAt",
            updatedAt: "internalControl.updatedAt",
          },
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: [
            "companyName",
            "email",
            "phone",
            "taxId",
            "contactPerson",
            "code",
          ],
          hasStatusIndex: false,
          customRules: [supplierStateRule, createTagFilterRule("suppliers")],
          withTags: true,
          searchIndex: {
            name: "search_suppliers",
            field: "companyName",
          },
        },
      ],
      readOnlyTables: [],
    },
  ],
};
